import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { useProviderDemographicsQuery } from '@sde/widgets-core/src/common/hooks/useProviderDemographicsQuery';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { styled } from '@uhg-abyss/web/tools/styled';
import { RightRailSubCard } from 'src/common/RightRailSubCard';
import { homeContentLinksWithHealthPlanFormatter } from '../../helpers';

const CARD_STYLES = {
  height: '600px',
  border: 'none',
};

const FLEX_TOP_STYLES = {
  paddingLeft: '18px',
  paddingRight: '18px',
  height: '45%',
};

const FLEX_BOTTOM_STYLES = {
  paddingLeft: '18px',
  paddingRight: '18px',
  height: '53%',
};

const LOADING_OVERLAY_STYLES = {
  'abyss-loading-overlay-root': {
    overflow: 'hidden',
  },
};

const HOME_PAGE_CONTENTS_HIGHER_CARD_KEYS = [
  'MEBTOOLRESOURCES',
  'MEBINTERACTIVETRAININGGUIDE',
  'MEBDRUGLISTSANDPHARMACY',
  'HEALTHPLANS',
];

const HOME_PAGE_CONTENTS_LOWER_CARD_KEYS = [
  'MEBUMRLINK',
  'MEBALLSAVERSLINK',
  'MEBCOMMUNITYCARENETWORKLINK',
  'MEBOPTUMPHYSICALHEALTHLINK',
];

const Divider = styled('div', {
  width: '100%',
  borderBottom: '1px solid $gray4',
});

export const AdditionalLinks = ({ user, toggleModal }) => {
  const { uuid, provider } = user;
  const { corporateProviderMpin, taxIdNumber } = provider;
  const [response, getProviderDemographics] = useProviderDemographicsQuery({});
  const { getHomePageContentsByArrayOfKeys } = usePPDLagoon();
  const { data, loading } = response;

  const higherCardLinks = useMemo(
    () =>
      homeContentLinksWithHealthPlanFormatter(
        getHomePageContentsByArrayOfKeys(HOME_PAGE_CONTENTS_HIGHER_CARD_KEYS),
        data
      ),
    [getHomePageContentsByArrayOfKeys, data]
  );

  const lowerCardLinks = useMemo(
    () => getHomePageContentsByArrayOfKeys(HOME_PAGE_CONTENTS_LOWER_CARD_KEYS),
    [getHomePageContentsByArrayOfKeys]
  );

  useEffect(() => {
    if (uuid && taxIdNumber && corporateProviderMpin) {
      getProviderDemographics({
        variables: {
          uuid,
          tin: taxIdNumber,
          corporateProviderMpin,
        },
      });
    }
  }, [uuid, corporateProviderMpin, taxIdNumber]);

  return (
    <LoadingOverlay
      isLoading={loading}
      loadingMessage="Retrieving Additional Links"
      isOpen={loading}
      css={LOADING_OVERLAY_STYLES}
    >
      <Card css={CARD_STYLES}>
        <Flex style={FLEX_TOP_STYLES} direction="column" justify="space-evenly">
          <RightRailSubCard
            railHeader="Eligibility & Benefits Resources"
            railLinks={higherCardLinks}
            user={user}
            toggleModal={toggleModal}
          />
        </Flex>
        <Divider />
        <Flex
          style={FLEX_BOTTOM_STYLES}
          direction="column"
          justify="space-evenly"
        >
          <RightRailSubCard
            railHeader="Quick Links & Tools"
            railLinks={lowerCardLinks}
            hideDivider
            user={user}
            toggleModal={toggleModal}
          />
        </Flex>
      </Card>
    </LoadingOverlay>
  );
};

AdditionalLinks.propTypes = {
  user: PropTypes.objectOf(PropTypes.any).isRequired,
  toggleModal: PropTypes.func.isRequired,
};
